import React, { useCallback, useEffect, useState, FormEvent } from 'react';
import axiosInstance from 'config/axiosInstance';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import {
  businessDateTimeToIsoKeeping,
  businessMonth,
  businessRangeQuery,
  monthBounds,
  formatBusiness,
  isoToBusinessDate,
  isoToBusinessTime,
} from 'config/time';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import { IconEdit } from '@tabler/icons-react';
import EditDialog from 'reusable/Dialog/editDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import Search from 'reusable/search';
import { PAGE_SIZE } from 'config/constant';
import { useTranslation } from 'react-i18next';
import DeviceTimeZone, { deviceTimeZoneOf, isAdminEnteredEnd } from 'reusable/DeviceTimeZone';
import { useSaveError } from 'reusable/useSaveError';
import { correctionReason, correctionReasonField } from 'components/Attendance/AttendanceField';
import { pageToRecover } from 'reusable/pageRecovery';
import { useMonthPage } from 'reusable/useMonthPage';


interface DriverWorkingRecordTableProps {
  id?: string;
  /** A36（2026-11-03 和田様の UAT）。ドライバー詳細の上部の月の切り替えで選んだ月（`YYYY-MM`）。既定は今の月。 */
  month?: string;
}

interface DriverWorkingRecord {
  id: string;
  start_time: string;
  /** 実施中の記録では未設定。 */
  stop_time: string | null;
  client_delivery: string;
  task: string;
  /** APIが返す配送先・作業の名称。ADM-06。 */
  client_delivery_info?: { id: number; name: string };
  task_info?: { id: number; name: string };
}

interface ClientTaskNames {
  clientName: string;
  taskName: string;
}

/** 入力欄の値を文字列として扱う。未入力はnull。 */
const asText = (value: string | number | null | undefined): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

const DriverWorkingRecordTable: React.FC<DriverWorkingRecordTableProps> = ({
  id,
  month = businessMonth(),
}) => {
  const { t, i18n } = useTranslation('driver');
  // 既定は新しい順（サーバーの並び順）。もう一方は記録の時刻の古い順。
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.oldest', { ns: 'common' }), value: 'start_time' },
  ];
  const [currentPage, setCurrentPage] = useMonthPage(month);
  const [sortValue, setSortValue] = useState('');
  const [count, setCount] = useState(0);
  const [driverWorkingRecord, setDriverWorkingRecord] = useState<
    DriverWorkingRecord[]
  >([]);
  const [searchValue, setSearchValue] = useState('');
  const [clientTaskNames, setClientTaskNames] = useState<ClientTaskNames[]>([]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { showSaveError, saveErrorPopup } = useSaveError();
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentWorkingRecordId, setCurrentWorkingRecordId] = useState<
    string | null
  >(null);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const formatDate = (datetime: string | null) => {
    // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
    return formatBusiness(
      datetime,
      i18n.language === 'jp' ? 'yyyy/MM/dd | HH:mm' : 'dd MMM yyyy | HH:mm',
      '-'
    );
  };

  // EV-322。配送先・作業の名前を読めなかったこと（行は残し、表の上に知らせる）。
  const [namesError, setNamesError] = useState<unknown>(null);

  const fetchClientAndTaskNames = useCallback(
    async (records: DriverWorkingRecord[], isCurrent: () => boolean = () => true) => {
      try {
        const clientTaskNamesPromises = records.map(async (record) => {
          const clientPromise = axiosInstance.get(
            `/v1/client_deliveries/${record.client_delivery}/`
          );
          const taskPromise = axiosInstance.get(`/v1/tasks/${record.task}/`);

          const [clientResponse, taskResponse] = await Promise.all([
            clientPromise,
            taskPromise,
          ]);

          return {
            clientName: clientResponse.data.name,
            taskName: taskResponse.data.name,
          };
        });

        const names = await Promise.all(clientTaskNamesPromises);
        if (!isCurrent()) return;
        setClientTaskNames(names);
      } catch (error) {
        // EV-322（ADM-15）。名前を読めなかったことを出す（以前は console だけで、名前が「-」と出た）。
        // 前の一覧の名前を今の行に残さない。切り替えた後に届いた失敗は捨てる。
        if (!isCurrent()) return;
        console.error('Failed to fetch client and task names', error);
        setClientTaskNames([]);
        setNamesError(error);
      }
    },
    []
  );

  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      // A36。選んだ月の業務日の範囲（作業の開始の時刻）。
      const { start, end } = monthBounds(month);
      let apiUrl = '/v1/task_sessions/?';
      apiUrl += businessRangeQuery('start_time', start, end);
      apiUrl += `order_by=${sortValue}&search=${searchValue}&page=${currentPage}&page_size=${PAGE_SIZE}&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      if (!isCurrent()) return;
      setFetchError(null);
      setNamesError(null);
      setDriverWorkingRecord(response.data.results);
      setCount(response.data.count);

      await fetchClientAndTaskNames(response.data.results, isCurrent);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, PAGE_SIZE, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch data', error);
      setDriverWorkingRecord([]);
      setNamesError(null);
      setFetchError(error);
    }
  }, [fetchClientAndTaskNames, sortValue, searchValue, month, currentPage, id, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createEditDialogFields = (
    record: DriverWorkingRecord
  ): DialogField[] => {
    // ADM-04。表示は +9h して切り出し、保存は `Z` を付けて結合していたため、
    // 編集して保存するたびに時刻が9時間ずれていた。変換はtime.tsに揃える。
    const startDate = isoToBusinessDate(record.start_time);
    const startTime = isoToBusinessTime(record.start_time);
    const endDate = isoToBusinessDate(record.stop_time);
    const endTime = isoToBusinessTime(record.stop_time);

    return [
      {
        label: t('work-record-edit.client-name'),
        name: 'clientName',
        // ADM-06。配列の先頭ではなく、編集対象の記録に紐づく名前を出す。
        value: record.client_delivery_info?.name ?? '',
      },
      {
        label: t('work-record-edit.task-name'),
        name: 'taskName',
        value: record.task_info?.name ?? '',
      },
      {
        label: t('work-record-edit.start-date'),
        name: 'startDate',
        value: startDate,
        type: 'date',
      },
      {
        label: t('work-record-edit.start-time'),
        name: 'startTime',
        value: startTime,
        type: 'time',
        // EV-116（照合の懸念4）。元の時刻。変えずに保存するときの前後の検査に使う。
        originalIso: record.start_time ?? null,
      },
      { label: t('work-record-edit.end-date'), name: 'endDate', value: endDate, type: 'date' },
      {
        label: t('work-record-edit.end-time'),
        name: 'endTime',
        value: endTime,
        type: 'time',
        originalIso: record.stop_time ?? null,
      },
      // BE-09-AC5（RA-AC1）。修正の理由（サーバーで必須）。
      correctionReasonField(t),
    ];
  };

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
  };

  const closeDialog = () => {
    setIsEditDialogOpen(false);
    setCurrentWorkingRecordId(null);
  };

  const handleDialog = async (
    dialogType: DialogType,
    record: DriverWorkingRecord
  ) => {
    console.log(record);
    if (dialogType === DialogType.Edit) {
      setCurrentWorkingRecordId(record.id);
      openDialog(
        t('view.working-record.edit-title'),
        '',
        createEditDialogFields(record),
        DialogType.Edit
      );
    }
  };

  const handleEditWorkingRecord = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    // 変えていない項目は元の値（秒・ミリ秒を含む）をそのまま送る。
    const original = driverWorkingRecord.find(
      (record) => String(record.id) === String(updatedData.id)
    );
    const startDateTime = businessDateTimeToIsoKeeping(
      asText(updatedData.startDate),
      asText(updatedData.startTime),
      original?.start_time
    );
    const endDateTime = businessDateTimeToIsoKeeping(
      asText(updatedData.endDate),
      asText(updatedData.endTime),
      original?.stop_time
    );

    // ADM-04。実施中（終了時刻が空）の記録に不正な時刻を入れない。
    const formattedUpdatedData: Record<string, string> = {};
    if (startDateTime) formattedUpdatedData.start_time = startDateTime;
    if (endDateTime) formattedUpdatedData.stop_time = endDateTime;
    // BE-09-AC5（RA-AC3）。修正の理由（サーバーで必須。修正の前後と一緒に記録される）。
    formattedUpdatedData.reason = correctionReason(updatedData);

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/task_sessions/${updatedData.id}/`,
          formattedUpdatedData
        );

        setDriverWorkingRecord((prevData) =>
          prevData.map((record) =>
            record.id === updatedData.id ? response.data : record
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update working record:', error);
      // 訂正が拒否された理由（退勤が出勤より前など）を表示する。
      // ADM-15。ダイアログは開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(error);
    }
  };
  console.log('Driver working record:', driverWorkingRecord);
  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        <div className="flex w-2/5 justify-start">
          <Search
            placeholder={t('ui.work-search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="flex flex-row gap-2">
          <SortBy
            options={sortingOptions}
            defaultValue={sortValue}
            onChange={handleSortChange}
          />
        </div>
      </div>

      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1 text-center">
            {t('view.working-record.client-name')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-record.task-name')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-record.start')}
          </h6>
          <h6 className="flex-1 text-center">{t('view.working-record.end')}</h6>
          <h6 className="flex-1 text-center">
            {t('time-zone.device', { ns: 'common' })}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-record.action')}
          </h6>
        </div>
        <div>
          {fetchError ? (
            <FetchFailed error={fetchError} onRetry={fetchData} />
          ) : namesError ? (
            <FetchFailed
              error={namesError}
              onRetry={fetchData}
              message={t('ui.detail-fetch-failed', { ns: 'common' })}
            />
          ) : null}
          {driverWorkingRecord.map((record, index) => (
            <div
              key={index}
              className="grid grid-cols-6 gap-4 px-4 py-3 border-b items-center"
            >
              <div className="flex justify-center">
                {clientTaskNames[index]?.clientName || '-'}
              </div>
              <div className="flex justify-center">
                {clientTaskNames[index]?.taskName || '-'}
              </div>
              <div className="flex justify-center">
                {formatDate(record.start_time)}
              </div>
              <div className="flex justify-center">
                {formatDate(record.stop_time)}
              </div>
              {/* EV-116 段階3。開始・停止の時点の端末の時間帯（同じ列に2つ。既存の見出しの文言で区別）。 */}
              <div className="flex flex-col items-center text-center">
                <DeviceTimeZone
                  {...deviceTimeZoneOf(record)}
                  at={record.start_time}
                  label={t('view.working-record.start')}
                />
                {record.stop_time && (
                  <DeviceTimeZone
                    {...deviceTimeZoneOf(record, 'stop_')} adminEntered={isAdminEnteredEnd(record, 'stop_')}
                    at={record.stop_time}
                    label={t('view.working-record.end')}
                  />
                )}
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  className="inline-flex items-center"
                  aria-label={t('ui.edit', { ns: 'common' })}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDialog(DialogType.Edit, record);
                  }}
                >
                  <IconEdit
                    size={20}
                    stroke={1.5}
                    className="cursor-pointer"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <EditDialog
        isOpen={isEditDialogOpen}
        onClose={closeDialog}
        handleSubmit={(e: any, updatedData: any) =>
          handleEditWorkingRecord(e, {
            ...updatedData,
            id: currentWorkingRecordId ?? '',
          })
        }
        confirmLabel={t('edit.save')}
        cancelLabel={t('edit.cancel')}
        title={dialogProps.title}
        subtitle={dialogProps.subtitle ?? ''}
        fields={dialogProps.fields}
        taskSessionId={currentWorkingRecordId ?? ''}
      />

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
      {saveErrorPopup}
    </section>
  );
};

export default DriverWorkingRecordTable;
